import { useState } from 'react';
import { useQuery, useMutation, gql } from '@apollo/client';
import { Container, Header, Segment, Button, Message, Label, Icon, Dropdown, Divider, Input, Table } from 'semantic-ui-react';
import { Navigate } from 'react-router-dom';

import { ResponsiveContainer } from '../../../components/ResponsiveContainer';
import { PATHS } from '../../../paths';
import { RoleEditor, type Entry } from './RoleEditor';

/**
 * Phase 4B.2 — the roles page.
 *
 * Two things that look similar and are not:
 *
 *   - **Who holds a role** changes often and affects one person.
 *   - **What a role means** changes rarely and affects *everyone* holding it, retroactively,
 *     because expansion rewrites grants from the template.
 *
 * This page does the first and shows the second read-only. Editing definitions is 4B.3/4B.4,
 * which needs a preview first: a one-row change can move thousands of grants, and this project
 * has spent most of its time on exactly that kind of silent movement.
 */

const GET_ROLES = gql`
  query RoleDefinitions {
    canAdministerRoles
    grantableResourceTypes
    grantableLevels
    capabilityKeys
    scopeTagOptions { id name }
    roleDefinitions {
      role
      name
      description
      isDefined
      entries { id resourceType level scoped capability isBuiltIn }
      holders { assignmentId membershipId userId name userName scopeTagId scopeTagName }
    }
    rolePresets { key name description roles isApplied }
    directGrants {
      id membershipId userId name userName
      resourceType level sourceType shape tags reaches isRedundant isOnlyRecordOfTeaching
    }
  }
`;

const UNASSIGN = gql`
  mutation UnassignRole($assignmentId: Int!) {
    unassignRole(assignmentId: $assignmentId) { status }
  }
`;

const GET_PEOPLE = gql`
  query AdminGetAllUsers {
    adminGetAllUsers { id name identifier }
  }
`;

const ASSIGN = gql`
  mutation AssignRole($userId: Int!, $role: String!, $scopeTagId: Int) {
    assignRole(userId: $userId, role: $role, scopeTagId: $scopeTagId) { status }
  }
`;

const REVOKE_GRANT = gql`
  mutation RevokeDirectGrant($grantId: Int!) {
    revokeDirectGrant(grantId: $grantId) { status }
  }
`;

const CREATE_ROLE = gql`
  mutation CreateRole($name: String!, $entries: [RoleTemplateEntryInput!]!) {
    createRole(name: $name, entries: $entries) { status }
  }
`;

const APPLY_PRESET = gql`
  mutation ApplyRolePreset($preset: String!) {
    applyRolePreset(preset: $preset) { status }
  }
`;


/**
 * A role's name for display.
 *
 * Built-in roles carry one from `ROLES`; a role somebody created here does not, and showing the
 * raw key means reading COMPANION in shouting caps. Deriving it keeps custom and built-in roles
 * looking alike without a second stored field to keep in step.
 */
const roleLabel = (role: string, name: string | null): string =>
  name ?? role.toLowerCase().replace(/_/g, ' ').replace(/^./, (c) => c.toUpperCase());

/** What one template row grants, in words rather than in column names. */
const describeEntry = (e: any): string => {
  if (e.resourceType === 'CAPABILITY') {
    return (e.capability ?? '').toLowerCase().replace(/_/g, ' ');
  }
  const what = e.resourceType.toLowerCase();
  const where = e.scoped ? 'their own' : 'every';
  return `${e.level.toLowerCase()} ${where} ${what}`;
};

const RolesView = () => {
  const { data, loading, error, refetch } = useQuery(GET_ROLES, { fetchPolicy: 'network-only' });
  const [unassign] = useMutation(UNASSIGN);
  const [assign, { loading: assigning }] = useMutation(ASSIGN);
  const { data: peopleData } = useQuery(GET_PEOPLE);
  const [adding, setAdding] = useState<string | null>(null);
  const [addPerson, setAddPerson] = useState<number | null>(null);
  const [addScope, setAddScope] = useState<number | null>(null);
  const [applyPreset, { loading: applying }] = useMutation(APPLY_PRESET);
  const [createRole, { loading: creating }] = useMutation(CREATE_ROLE);
  const [revokeGrant] = useMutation(REVOKE_GRANT);
  const [newRoleName, setNewRoleName] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [editing, setEditing] = useState<string | null>(null);

  if (loading) {
    return <ResponsiveContainer><Container><p>Loading…</p></Container></ResponsiveContainer>;
  }

  // `canAdministerRoles` is the server's answer to the same `mayGrant` test the mutations
  // enforce. Gating on it rather than on a local role check keeps the page and the mutations
  // from drifting — a page that shows buttons the server will refuse is worse than no page.
  if (error || !data?.canAdministerRoles) {
    return <Navigate to={PATHS.CLASSES} replace />;
  }

  const roles = data.roleDefinitions ?? [];
  const presets = data.rolePresets ?? [];

  return (
    <ResponsiveContainer>
      <Container style={{ paddingBottom: '3em' }}>
        <Header as="h1">Roles</Header>
        <p style={{ color: '#767676' }}>
          What each role grants in this organisation, and who holds it. Changing what a role
          means affects everyone holding it.
        </p>

        {actionError && <Message negative onDismiss={() => setActionError(null)}>{actionError}</Message>}

        <Segment>
          <Header as="h3">Role sets</Header>
          <p style={{ color: '#767676' }}>
            Add a standard set of roles. Adding a set only fills in what is missing — it never
            changes or removes a role you already have.
          </p>
          {presets.map((p: any) => (
            <div key={p.key} style={{ marginBottom: '1em' }}>
              <strong>{p.name}</strong>
              {p.isApplied && <Label color="green" size="tiny" style={{ marginLeft: '0.6em' }}>added</Label>}
              <div style={{ color: '#767676', fontSize: '0.95em', margin: '0.2em 0 0.5em' }}>
                {p.description}
              </div>
              <Button
                size="small"
                disabled={p.isApplied || applying}
                loading={applying}
                onClick={async () => {
                  setActionError(null);
                  try {
                    await applyPreset({ variables: { preset: p.key } });
                    await refetch();
                  } catch (e: any) {
                    setActionError(e?.message ?? 'Could not add those roles.');
                  }
                }}
              >
                Add these roles
              </Button>
            </div>
          ))}
        </Segment>

        <Segment>
          <Header as="h3">Make a new role</Header>
          <p style={{ color: '#767676' }}>
            For something the standard roles do not cover — a companion who sees one community,
            for instance.
          </p>
          {newRoleName == null
            ? <Button size="small" onClick={() => setNewRoleName('')}>New role</Button>
            : (
              <div>
                <Input
                  placeholder="What is it called?"
                  value={newRoleName}
                  style={{ marginRight: '0.5em' }}
                  onChange={(e) => setNewRoleName(e.target.value)}
                />
                <Button
                  primary
                  loading={creating}
                  disabled={creating || newRoleName.trim() === ''}
                  onClick={async () => {
                    setActionError(null);
                    try {
                      // Created with no permissions, then filled in through the ordinary
                      // editor — so a new role goes through the same preview as any change,
                      // rather than having a second path that skips it.
                      await createRole({ variables: { name: newRoleName, entries: [] } });
                      setNewRoleName(null);
                      await refetch();
                    } catch (e: any) {
                      setActionError(e?.message ?? 'Could not make that role.');
                    }
                  }}
                >
                  Create
                </Button>
                <Button onClick={() => setNewRoleName(null)}>Cancel</Button>
                <p style={{ color: '#767676', fontSize: '0.9em', marginTop: '0.5em' }}>
                  It starts with no permissions. Add them below once it exists.
                </p>
              </div>
            )}
        </Segment>

        {roles.map((r: any) => (
          <Segment key={r.role}>
            <Header as="h3" style={{ marginBottom: '0.2em' }}>
              {roleLabel(r.role, r.name)}
              {!r.isDefined && (
                <Label color="orange" size="tiny" style={{ marginLeft: '0.6em' }}>
                  grants nothing
                </Label>
              )}
            </Header>
            {r.description && (
              <p style={{ color: '#767676', fontSize: '0.95em' }}>{r.description}</p>
            )}

            {/* A role somebody holds that grants nothing is the failure this page exists to make
                visible — it is how the untemplated partner roles were found. */}
            {!r.isDefined && r.holders.length > 0 && (
              <Message warning size="tiny">
                {r.holders.length} {r.holders.length === 1 ? 'person holds' : 'people hold'} this
                role, but it has no permissions, so it currently gives them nothing.
              </Message>
            )}

            <Header as="h5">Grants</Header>
            {editing === r.role
              ? (
                <RoleEditor
                  role={r.role}
                  initial={r.entries.map((e: any): Entry => ({
                    resourceType: e.resourceType,
                    level: e.level,
                    scoped: e.scoped,
                    capability: e.capability,
                  }))}
                  resourceTypes={data.grantableResourceTypes ?? []}
                  levels={data.grantableLevels ?? []}
                  capabilities={data.capabilityKeys ?? []}
                  onDone={async () => { setEditing(null); await refetch(); }}
                  onCancel={() => setEditing(null)}
                />
              )
              : (
                <>
                  {r.entries.length === 0
                    ? <p style={{ color: '#767676' }}>Nothing.</p>
                    : (
                      <ul style={{ margin: '0 0 1em', paddingLeft: '1.2em' }}>
                        {r.entries.map((e: any) => (
                          <li key={e.id} style={{ marginBottom: '0.15em' }}>{describeEntry(e)}</li>
                        ))}
                      </ul>
                    )}
                  <Button size="small" onClick={() => setEditing(r.role)}>
                    Change what this role grants
                  </Button>
                </>
              )}

            <Header as="h5">
              Held by {r.holders.length > 0 && <span style={{ fontWeight: 400 }}>({r.holders.length})</span>}
            </Header>
            {/* A scoped role grants over the things carrying its scope tag, so it needs one:
                a centre manager is a manager *of* a community. Which roles are scoped comes
                from the template rather than a hardcoded list, so a role that becomes scoped
                in the editor starts asking for a tag here without anything else changing. */}
            {adding === r.role
              ? (
                <Segment secondary>
                  <Dropdown
                    search
                    selection
                    clearable
                    placeholder="Choose a person"
                    style={{ marginRight: '0.5em', minWidth: '16em' }}
                    value={addPerson ?? undefined}
                    options={(peopleData?.adminGetAllUsers ?? []).map((u: any) => ({
                      key: u.id,
                      text: u.identifier ? `${u.name} (${u.identifier})` : u.name,
                      value: u.id,
                    }))}
                    onChange={(_, d) => setAddPerson(d.value as number)}
                  />
                  {r.entries.some((e: any) => e.scoped) && (
                    <Dropdown
                      search
                      selection
                      clearable
                      placeholder="Of which community"
                      style={{ marginRight: '0.5em', minWidth: '14em' }}
                      value={addScope ?? undefined}
                      options={(data.scopeTagOptions ?? []).map((t: any) => ({
                        key: t.id, text: t.name, value: t.id,
                      }))}
                      onChange={(_, d) => setAddScope(d.value as number)}
                    />
                  )}
                  <Button
                    primary
                    loading={assigning}
                    disabled={
                      assigning
                      || addPerson == null
                      || (r.entries.some((e: any) => e.scoped) && addScope == null)
                    }
                    onClick={async () => {
                      setActionError(null);
                      try {
                        await assign({
                          variables: {
                            userId: addPerson,
                            role: r.role,
                            scopeTagId: r.entries.some((e: any) => e.scoped) ? addScope : null,
                          },
                        });
                        setAdding(null);
                        setAddPerson(null);
                        setAddScope(null);
                        await refetch();
                      } catch (e: any) {
                        setActionError(e?.message ?? 'Could not give that person this role.');
                      }
                    }}
                  >
                    Give this role
                  </Button>
                  <Button onClick={() => { setAdding(null); setAddPerson(null); setAddScope(null); }}>
                    Cancel
                  </Button>
                  {r.entries.some((e: any) => e.scoped) && (
                    <p style={{ color: '#767676', fontSize: '0.9em', marginTop: '0.6em' }}>
                      This role applies to one community at a time. To cover more than one, add
                      the person again for each.
                    </p>
                  )}
                </Segment>
              )
              : (
                <Button
                  size="small"
                  style={{ marginBottom: '0.8em' }}
                  onClick={() => { setAdding(r.role); setAddPerson(null); setAddScope(null); }}
                >
                  Give someone this role
                </Button>
              )}

            {r.holders.length === 0
              ? <p style={{ color: '#767676' }}>Nobody.</p>
              : (
                <div>
                  {r.holders.map((h: any) => (
                    <Label key={h.assignmentId} style={{ margin: '0 0.4em 0.4em 0' }}>
                      {h.name ?? h.userName ?? `user ${h.userId}`}
                      {h.scopeTagName && (
                        <span style={{ fontWeight: 400, color: '#767676' }}> · {h.scopeTagName}</span>
                      )}
                      <Icon
                        name="delete"
                        onClick={async () => {
                          setActionError(null);
                          try {
                            await unassign({ variables: { assignmentId: h.assignmentId } });
                            await refetch();
                          } catch (e: any) {
                            setActionError(e?.message ?? 'Could not remove that role.');
                          }
                        }}
                      />
                    </Label>
                  ))}
                </div>
              )}
          </Segment>
        ))}

        {/* 4B.5 — access that comes from no role.
            These are the leftovers of the formula migration plus anything granted by hand.
            Listed here as one worklist rather than scattered across individual profiles, where
            in practice nobody would ever find them. */}
        {(data.directGrants ?? []).length > 0 && (
          <Segment>
            <Header as="h3">Access that comes from no role</Header>
            <p style={{ color: '#767676' }}>
              Granted directly to one person, or left over from before roles existed. Rows
              marked <em>adds nothing</em> are already covered by that person's roles, so
              removing one changes nothing they can do.
            </p>
            <Table compact size="small">
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell>Person</Table.HeaderCell>
                  <Table.HeaderCell>Can</Table.HeaderCell>
                  <Table.HeaderCell>Over</Table.HeaderCell>
                  <Table.HeaderCell>Reaches</Table.HeaderCell>
                  <Table.HeaderCell>From</Table.HeaderCell>
                  <Table.HeaderCell />
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {(data.directGrants ?? []).map((g: any) => (
                  <Table.Row key={g.id} warning={!g.isRedundant && g.reaches > 0}>
                    <Table.Cell>{g.name ?? g.userName ?? `user ${g.userId}`}</Table.Cell>
                    <Table.Cell>{g.level.toLowerCase()}</Table.Cell>
                    <Table.Cell>
                      {g.shape === 'wildcard' && `every ${g.resourceType.toLowerCase()}`}
                      {g.shape === 'direct' && `one ${g.resourceType.toLowerCase()}`}
                      {g.shape === 'dynamic' && (
                        g.tags.length > 0
                          ? `${g.resourceType.toLowerCase()}s tagged ${g.tags.join(' and ')}`
                          : `${g.resourceType.toLowerCase()}s (no tags — matches nothing)`
                      )}
                    </Table.Cell>
                    <Table.Cell>{g.reaches}</Table.Cell>
                    <Table.Cell>
                      {/* Three states, not two. "Adds nothing" is about access; a row can add
                          no access and still be the only record that somebody taught a class,
                          which is the one case where deleting it loses something. */}
                      {g.isOnlyRecordOfTeaching
                        ? <Label size="tiny" color="blue">only record of teaching</Label>
                        : g.isRedundant
                          ? <Label size="tiny">adds nothing</Label>
                          : <Label size="tiny" color={g.reaches > 0 ? 'orange' : undefined}>
                              {g.sourceType === 'MIGRATION' ? 'old formula' : 'granted by hand'}
                            </Label>}
                    </Table.Cell>
                    <Table.Cell collapsing textAlign="right">
                      <Icon
                        name="trash"
                        link
                        onClick={async () => {
                          setActionError(null);
                          try {
                            await revokeGrant({ variables: { grantId: g.id } });
                            await refetch();
                          } catch (e: any) {
                            setActionError(e?.message ?? 'Could not remove that.');
                          }
                        }}
                      />
                    </Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table>
            <p style={{ color: '#767676', fontSize: '0.9em' }}>
              Highlighted rows are the only thing giving that person that access — removing one
              takes it away. Rows marked <em>only record of teaching</em> name a class whose
              teacher is not recorded anywhere else: removing one loses that history, even though
              it changes nobody's access.
            </p>
          </Segment>
        )}

        <Divider />
        <p style={{ color: '#767676', fontSize: '0.9em' }}>
          Changing what a role grants affects everyone who holds it. You will always be shown
          what the change does before it is saved.
        </p>
      </Container>
    </ResponsiveContainer>
  );
};

export { RolesView };
